// Intelligent Agent Intent Parser and Dynamic Primitive Generator

export type AgentResponse = {
  thinkingVariant: 'Steps' | 'Reasoning' | 'Search' | 'Coding';
  thinkingSummary?: string;
  loadingLabel: string;
  loadingVariant: 'Drive' | 'Dots' | 'Orbit';
  tools?: {
    icon: string;
    label: string;
    chip: string;
    mono: boolean;
    detailMono: boolean;
    detail: { text: string; tone?: 'add' }[];
  }[];
  toolDiffs?: { file: string; add: number; del: number }[];
  taskRows?: {
    key: string;
    badgeTone?: 'green' | 'red';
    label: string;
    amount: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    details: { label: string; meta: string }[];
  }[];
  approvalQuestions?: {
    q: string;
    type: 'radio' | 'check';
    options: string[];
  }[];
  content: string;
  followups?: string[];
  hasChatComposer?: boolean;
};

export function parseAgentIntent(prompt: string, sources: string[] = []): AgentResponse {
  const p = prompt.toLowerCase();

  // 1. Coding / Bug / AST / Script Intent
  if (p.includes('code') || p.includes('script') || p.includes('bug') || p.includes('leak') || p.includes('hook') || p.includes('refactor') || p.startsWith('/code')) {
    return {
      thinkingVariant: 'Coding',
      thinkingSummary: 'Inspected event listener closures and generated leak-free AbortController pattern.',
      loadingLabel: 'Profiling memory heap',
      loadingVariant: 'Drive',
      tools: [
        {
          icon: 'read',
          label: 'Read source',
          chip: 'useScroll.ts',
          mono: true,
          detailMono: true,
          detail: [
            { text: '124 lines · found unremoved scroll listener' },
            { text: 'Closure retains entire component scope' }
          ]
        },
        {
          icon: 'write',
          label: 'Apply refactor',
          chip: 'useScrollOptimized.ts',
          mono: true,
          detailMono: true,
          detail: [
            { text: '+ const controller = new AbortController();', tone: 'add' },
            { text: '+ return () => controller.abort();', tone: 'add' }
          ]
        },
        {
          icon: 'run',
          label: 'Verify tests',
          chip: 'npm test hooks',
          mono: true,
          detailMono: true,
          detail: [
            { text: '✓ 18 unit tests passed' },
            { text: '✓ Peak heap decreased by 87%' }
          ]
        }
      ],
      toolDiffs: [
        { file: 'useScroll.ts', add: 24, del: 18 },
        { file: 'hooks.test.ts', add: 12, del: 2 }
      ],
      taskRows: [
        {
          key: 'ast',
          label: 'Parse AST dependency tree',
          amount: '1 hook',
          status: 'completed',
          details: [{ label: 'Closure depth', meta: '2 levels' }, { label: 'Teardown guard', meta: 'Missing' }]
        },
        {
          key: 'heap',
          label: 'Run simulated heap snapshot',
          amount: '10,000 samples',
          status: 'completed',
          details: [{ label: 'Before leak', meta: '48MB' }, { label: 'After teardown', meta: '6.2MB' }]
        }
      ],
      content: `The memory leak is caused by an uncleaned event listener on the window object.

Here is the verified **AbortController** solution with micro-task debouncing:

\`\`\`typescript
import { useEffect, useState } from 'react';

export function useOptimizedScroll(threshold = 50) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > threshold);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, {
      passive: true,
      signal: controller.signal
    });

    return () => controller.abort();
  }, [threshold]);

  return isScrolled;
}
\`\`\`

All 18 automated regression checks have passed.`,
      followups: [
        'Add TypeScript strict generic typings',
        'Compare performance against requestAnimationFrame loop',
        'Write automated memory test in Vitest'
      ]
    };
  }

  // 2. Web Search / Market Research Intent
  if (p.includes('search') || p.includes('market') || p.includes('trend') || p.includes('supplier') || p.startsWith('/search') || p.includes('@web')) {
    return {
      thinkingVariant: 'Search',
      thinkingSummary: 'Scanned 14 industry directories and indexed commercial cone suppliers.',
      loadingLabel: 'Scanning web sources',
      loadingVariant: 'Orbit',
      tools: [
        {
          icon: 'read',
          label: 'Web query',
          chip: 'global gelato suppliers 2026',
          mono: false,
          detailMono: false,
          detail: [
            { text: 'Retrieved 8 commercial dairy & cone vendors' },
            { text: 'Verified HACCP & cold-chain certifications' }
          ]
        }
      ],
      taskRows: [
        {
          key: 'query',
          label: 'Index supplier pricing & lead times',
          amount: '8 vendors',
          status: 'completed',
          details: [{ label: 'Joy Cone Co.', meta: '7 days' }, { label: 'The Konery', meta: '5 days' }]
        },
        {
          key: 'margin',
          label: 'Compute wholesale discount brackets',
          amount: 'Tier 1',
          status: 'completed',
          details: [{ label: 'Bulk discount', meta: '-14%' }, { label: 'Min order', meta: '1,000 units' }]
        }
      ],
      content: `Here is the verified supplier intelligence breakdown:

* **Joy Cone Company**: Lowest unit price at $0.28/cone with reliable 7-day fulfillment SLA.
* **The Konery**: Premium artisanal waffle cones (vanilla bean, lavender, chocolate lavender) at $0.44/cone with 5-day rush delivery.
* **WebstaurantStore**: Commercial bulk supplier with instant dispatch for emergency stockouts.`,
      followups: [
        'Compare Joy Cone vs The Konery pricing for 5,000 units',
        'Draft a bulk procurement inquiry email',
        'Check supplier ratings on Trustpilot'
      ]
    };
  }

  // 3. Planning / Operations / Churn / Reorder Intent (Default Core Beautiful UI scenario)
  return {
    thinkingVariant: p.includes('reason') ? 'Reasoning' : 'Steps',
    thinkingSummary: 'Synthesized 3 summers of mint chip sales, computed demand curves, and drafted reorder actions.',
    loadingLabel: 'Calculating churn schedule',
    loadingVariant: 'Drive',
    tools: [
      {
        icon: 'think',
        label: 'Thinking',
        chip: 'Planning the churn schedule…',
        mono: false,
        detailMono: false,
        detail: [
          { text: 'Weekend demand carries pistachio (+18%), so it churns first.' },
          { text: 'Batch capacity leaves two evening freezer windows.' }
        ]
      },
      {
        icon: 'write',
        label: 'Write 204 lines',
        chip: 'ChurnSchedule.tsx',
        mono: true,
        detailMono: true,
        detail: [
          { text: '+ const windows = slots.filter((s) => s.temp <= -12)', tone: 'add' },
          { text: '+ return schedule(windows, { hero: "pistachio" })', tone: 'add' }
        ]
      },
      {
        icon: 'run',
        label: 'Rebuild and verify',
        chip: 'npm run freeze',
        mono: true,
        detailMono: true,
        detail: [
          { text: '✓ built in 1.2s' },
          { text: '✓ 34 operational checks passed' }
        ]
      }
    ],
    toolDiffs: [
      { file: 'flavors.css', add: 13, del: 0 },
      { file: 'ChurnSchedule.tsx', add: 74, del: 41 },
      { file: 'menu.ts', add: 8, del: 2 }
    ],
    taskRows: [
      {
        key: 'verify',
        label: 'Verified vendor records',
        amount: '12 suppliers',
        status: 'completed',
        details: [
          { label: 'Matched tax and contact IDs', meta: '12/12' },
          { label: 'Flagged stale records', meta: '0' }
        ]
      },
      {
        key: 'index',
        label: 'Build reorder task list',
        amount: '7 SKUs',
        status: 'completed',
        details: [
          { label: 'Reading POS export', meta: '3 files' },
          { label: 'Scoring stockout risk', meta: '68%' }
        ]
      },
      {
        key: 'draft',
        label: 'Draft supplier emails',
        amount: '2 messages',
        status: 'completed',
        details: [
          { label: 'Cone supplier follow-up', meta: 'draft' },
          { label: 'Pistachio reorder note', meta: 'draft' }
        ]
      }
    ],
    approvalQuestions: [
      {
        q: 'How many waffle cone cases should we order?',
        type: 'radio',
        options: ['1,500 units (Weekend buffer)', '3,000 units (Full month stock)', '500 units (Just emergency)']
      },
      {
        q: 'Which delivery priority?',
        type: 'radio',
        options: ['Standard 7 business days', 'Expedited 3 days (+$45)', 'Next-day courier']
      },
      {
        q: 'Authorize budget dispatch?',
        type: 'check',
        options: ['Debit warehouse budget #402', 'Send invoice copy to accounting', 'Notify floor manager']
      }
    ],
    content: `Pulled **3 summers of mint chip sales** for comparison. Mint chip is up **12%** with significantly stronger weekend peaks.

### Key Operational Recommendations
* **Pistachio Royale** holds the top slot all weekend (+18% velocity). Churn it first thing Saturday so the batch has time to firm up before the afternoon rush.
* **Rocky Road** is down -11% and is recommended for seasonal retirement.
* **Waffle Cone Inventory** is at 420 units, below the 1,200 threshold required for weekend foot traffic.

Please confirm the purchase order parameters in the approval card below to dispatch the order to **cone_king**.`,
    followups: [
      'Compare mint chip to last summer',
      'Which flavors sell best in winter?',
      'Export production batch sheet to CSV'
    ]
  };
}
